import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, Link, useHistory } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Spinner,
  Card,
  CardBody,
  Button,
  FormFeedback,
  Label,
  InputGroup,
  FormGroup,
  CustomInput,
  Input,
  Form,
  Progress,
} from 'reactstrap';

import { useStripe } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import es from 'date-fns/locale/es';
import clsx from 'clsx';
import * as Yup from 'yup';
import { Element, animateScroll as scroll, scroller } from 'react-scroll';

import styled from 'styled-components';
import MainLayout from '../layouts/MainLayout';
import mailIcon from '../images/pdf/file-send.svg';
import config from '../config';
import Select from 'react-select';
import DatePicker, { registerLocale } from 'react-datepicker';
import { EditorDocumentPreview } from '../components/DocumentPreview';
import { isPremiumTemplate as isPremiumTemplateFunc, getPrice } from '../utils';
import {
  EditorProvider,
  useEditorState,
  useEditor,
  useEditorActions,
  SET_PROGRESS,
  SET_DOCUMENT_PARTICIPANTS,
  SET_SHIELD_TYPE,
} from '../context/editor';
import { useAuthState } from '../context/auth';
import { useProfile } from '../api/hooks/profile';
import { useFetchSkuById } from '../api/hooks/stripe';
import EditTemplateConfiguration from '../components/EditTemplateConfiguration';
import useQueryParams from '../hooks/useQuery';
import LoadingButton from '../components/LoadingButton';
import NewEditor from '../components/NewEditor/NewEditor';

import { useFormik } from 'formik';
import InputWithLoader from '../components/InputWithLoader';

import {
  LegalParticipantSchema,
  IndividualParticipantSchema,
  getParticipantSchema,
} from '../components/ParticipantForm';
export const reactSelectStyles = {
  menu: (provided) => ({
    ...provided,
    zIndex: 3,
  }),
};
registerLocale('es', es);
const DatePickerContainer = styled.div`
  width: 100%;
  .react-datepicker-wrapper,
  input {
    width: 100%;
  }
`;

function CreateDocument() {
  const { t } = useTranslation('common');
  const [hasSucceed, setHasSucceed] = useState(false);
  const auth = useAuthState();
  const history = useHistory();
  const { can } = useProfile();

  const query = useQueryParams();

  const [editorState, editorDispatch] = useEditor();
  const { checkMissingSignatures } = useEditorActions();

  const { documentId, status, progress } = useEditorState();

  return (
    <MainLayout
      className="p-0"
      footer={false}
      paddingBottom={0}
      banner={
        !auth?.accessToken && {
          message: t('actions.newTemplates'),
          icon: 'file',
          color: 'white',
        }
      }
      showUpgradeBanner={false}
    >
      <>
        {documentId && status === 'success' && !hasSucceed && (
          <Container
            fluid
            className={clsx('overflow-hidden h-100')}
            style={{ maxWidth: '90%' }}
          >
            <Row noGutters className="w-100 align-items-center mt-3">
              <Col lg={9} className="pl-3">
                <div className="font-weight-bold font-size-32">
                  {editorState.document.name}
                </div>
              </Col>
              <Col
                lg={3}
                className="d-flex align-items-center"
                style={{ paddingRight: 15 }}
              >
                {/* <EditorDocumentPreview card header={false} /> */}
                <div className="d-flex flex-column justify-content-between align-items-center bg-white w-100 h-100 p-3 rounded">
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="font-size-18">Progreso </div>
                    <div className="text-muted">
                      {Math.min(progress * 100, 100)}%
                    </div>
                  </div>

                  <Progress
                    className="w-100"
                    value={Math.min(progress * 100, 100)}
                    color="accent-100"
                  />
                </div>
              </Col>
            </Row>
            <Row noGutters className="w-100 ">
              <Col lg={4} className="p-3 h-100">
                <Guide />
              </Col>
              <Col lg={8} className="overflow-auto">
                <EditorDocumentPreview
                  className="max-vh-75"
                  card
                  header={false}
                />
              </Col>
            </Row>
          </Container>
        )}
      </>
    </MainLayout>
  );
}

function CreateDocumentContainer() {
  const { publicTemplateId, templateId } = useParams();

  return (
    <EditorProvider
      documentId={publicTemplateId || templateId}
      mode={publicTemplateId ? 'public-template' : 'template'}
    >
      <CreateDocument />
    </EditorProvider>
  );
}

export default CreateDocumentContainer;

function determineParticipantSteps(participants) {
  return participants.reduce((total, p) => {
    if (p?.variables?.length > 0) total += 1;

    return total;
  }, participants.length);
}
function determineMaxProgress(participants) {
  return participants.reduce((total, p) => {
    total += p?.variables?.length || 0;
    return total;
  }, participants.length);
}

function Guide() {
  const [editorState, editorDispatch] = useEditor();
  const [currentStep, setCurrentStep] = useState(1);
  const [progress, setProgress] = useState(0);
  const [currentParticipantNumber, setCurrentParticipantNumber] = useState(0);
  const [currentVariableNumber, setCurrentVariableNumber] = useState(0);
  const auth = useAuthState();
  const { profile, can } = useProfile();
  // if its questionaire ask questions first
  // if its not ask variables
  const { participants, publicTemplate, shieldType } = editorState;
  const currentVariable = useMemo(
    () =>
      participants?.[currentParticipantNumber]?.variables?.[
        currentVariableNumber
      ],
    [participants, currentParticipantNumber, currentVariableNumber]
  );
  const totalSteps = useMemo(
    () => determineParticipantSteps(participants),
    [participants]
  );
  const maxProgress = useMemo(
    () => determineMaxProgress(participants),
    [participants]
  );
  const { t } = useTranslation();

  const Schema = useMemo(() => {
    if (participants) {
      return Yup.object().shape(
        participants.reduce((obj, p) => {
          obj[`p-${p._id}`] = getSchema(p);
          return obj;
        }, {})
      );
    }
    return {};
  }, [participants]);

  const initialValues = useMemo(
    () =>
      participants.reduce((obj, p) => {
        obj[`p-${p._id}`] = getInitialValues(p);
        return obj;
      }, {}),
    [participants]
  );
  const stripe = useStripe();

  const formik = useFormik({
    initialValues,
    onSubmit: async (values, actions) => {
      // const {agendaProperties, variables} = Object.entries(values.variables).reduce((obj, [key, value]) => {
      //   if (key.includes('agendaProperties')) {
      //     const [,propertyName] = key.split('-')
      //     obj.agendaProperties[propertyName] = value;
      //   } else {
      //     obj.variables[key] = value;
      //   }
      //   return obj;
      // }, { agendaProperties: {}, variables: {} });
      // console.log({values})
      // mutateAnswerForm({
      //   ...values,
      //   variables,
      //    agendaProperties,
      //   externalId: query.get('externalId'),
      //   formId,
      //   userFormId
      // })
      // actions.setSubmitting(false);
    },
    enableReinitialize: true,
    validationSchema: Schema,
  });
  const currentParticipant = useMemo(
    () => participants[currentParticipantNumber],
    [participants, currentParticipantNumber]
  );
  const totalVariables = useMemo(
    () => currentParticipant?.variables?.length || 0,
    [currentParticipant]
  );
  const totalParticipants = useMemo(
    () => participants?.length || 0,
    [participants]
  );
  const isPremiumTemplate = useMemo(() => {
    return isPremiumTemplateFunc({
      canTakePremiumTier1: can('TAKE_PREMIUM_TIER_1'),
      type: publicTemplate.type,
      premiumTier: publicTemplate.premiumTier,
    });
  }, [can, publicTemplate.type, publicTemplate.premiumTier]);

  const shield1Sku = useFetchSkuById('1_contracts_level_1_mx');
  const shield2Sku = useFetchSkuById('1_contracts_level_2_mx');

  const total = useMemo(() => {
    let total = 0;
    if (isPremiumTemplate) {
      total += publicTemplate?.stripeInfo?.price;
    }
    if (shieldType.number === 2) {
      total += shield2Sku?.data?.price;
    } else {
      total += shield1Sku?.data?.price;
    }
    return total;
  }, [
    isPremiumTemplate,
    publicTemplate.stripeInfo,
    shieldType.number,
    shield1Sku,
    shield2Sku,
  ]);

  const handleDateChange =
    (variableId, name = 'variables') =>
    (date) => {
      formik.setFieldValue(`${name}.${variableId}`, date);
    };

  const handleSelectChange =
    (variableId, name = 'variables') =>
    (option, action) => {
      formik.setFieldValue(`${name}.${variableId}`, option.value);
    };

  const updateDocumentState = () => {
    const values = formik.values;
    const newParticipants = participants.map((p) => {
      const { participant, variables } = values[`p-${p._id}`];

      return {
        ...p,
        ...participant,
        variables: p.variables.map((v) => ({
          ...v,
          value: variables[v._id],
        })),
      };
    });
    editorDispatch({
      type: SET_DOCUMENT_PARTICIPANTS,
      participants: newParticipants,
    });
  };

  const handleNext = async () => {
    const errors = await formik.validateForm();
    const currentParticipantErrors = errors[`p-${currentParticipant._id}`];

    if (
      currentStep === 1 &&
      Object.keys(currentParticipantErrors?.participant || {}).length
    ) {
      Object.keys(currentParticipantErrors.participant).map((key) => {
        formik.setFieldError(
          `p-${currentParticipant._id}.participant.${key}`,
          currentParticipantErrors.participant[key]
        );
        formik.setFieldTouched(
          `p-${currentParticipant._id}.participant.${key}`,
          true
        );
      });
      return;
    }
    const newCount = progress + 1;
    updateDocumentState();
    editorDispatch({ type: SET_PROGRESS, progress: newCount / maxProgress });
    setProgress(newCount);
    const newValue = currentVariableNumber + 1;

    console.log({ errors });
    // check if the Currentvariable is the last one
    if (newValue === totalVariables) {
      // Check if the currentParticipants is the last one
      if (currentParticipantNumber === totalParticipants - 1) {
        console.log('next step (ask to create user)');
        setCurrentStep(3);
        setCurrentVariableNumber(0);
        return;
      } else {
        setCurrentStep(1);
        setCurrentParticipantNumber((currentNumber) =>
          Math.min(currentNumber + 1, totalParticipants - 1)
        );
        setCurrentVariableNumber(0);
      }
      return;
    }
    if (currentStep === 2) {
      setCurrentVariableNumber(Math.min(newValue, totalVariables - 1));
    } else {
      const nextStep = currentStep + 1;

      setCurrentStep(
        nextStep === 2 && !currentParticipant?.variables.length
          ? 3
          : currentStep + 1
      );
    }
  };
  const handlePrev = () => {
    const newCount = progress - 1;
    editorDispatch({ type: SET_PROGRESS, progress: newCount / maxProgress });
    setProgress(newCount);
    if (currentStep === 1 && currentParticipantNumber === 0) {
      console.log('return step (introduction)');
      setCurrentStep(1);

      return;
    }

    if (
      currentStep === 3 ||
      (currentStep === 1 && currentParticipantNumber !== 0)
    ) {
      const idx =
        currentStep === 3
          ? currentParticipantNumber
          : currentParticipantNumber - 1;
      const prevParticipant = participants[idx];
      const variableCount = prevParticipant?.variables?.length;
      if (variableCount > 0) {
        setCurrentVariableNumber(variableCount - 1);
        setCurrentParticipantNumber(idx);
        setCurrentStep(2);
      } else {
        setCurrentStep(1);
        setCurrentParticipantNumber(idx);
      }
      return;
    }

    const newValue = currentVariableNumber - 1;
    // check if the current variable is the first one
    if (currentStep === 2 && newValue < 0) {
      setCurrentStep(1);
      return;
    } else {
      setCurrentVariableNumber(Math.max(newValue, 0));
    }
  };

  const handleStart = () => setCurrentStep(2);

  const purchase = async ({ items, publicContractId, customerEmail }) => {
    console.log({ items, customerEmail });
    await stripe.redirectToCheckout({
      successUrl: `${config.baseUrl}/checkout/success?productName=${publicTemplate.name}&type=contract-sent&goToMyDocuments=true`,
      //cancelUrl: `${config.baseUrl}/documents/${editorState?.documentId}/edit`,
      cancelUrl: `${config.baseUrl}/public-templates/${editorState.publicTemplate._id}/documents/create`,

      mode: 'payment',
      lineItems: items,
      clientReferenceId: `publicContract-${publicContractId}`,
      customerEmail,
    });
  };

  const handleSend = async () => {
    const items = [
      {
        price: `1_contracts_level_${shieldType.number}_mx`,
        quantity: 1,
      },
    ];
    if (isPremiumTemplate) {
      items.push({
        price: `${editorState.publicTemplate?.stripeId}_mx`,
        quantity: 1,
      });
    }

    if (!auth.hasAccount) {
      return auth.openAuthModal({
        type: 'signup',
        data: {
          publicContract: formik.values,
          publicTemplate: editorState.publicTemplate._id,
          name: editorState.publicTemplate.name,
          shieldType: editorState?.shieldType?.value === 'shield2' ? 2 : 1,
        },
        onLoginSuccess: async (data) => {
          await purchase({
            items,
            publicContractId: data?.publicContract?._id,
            customerEmail: data.user.email,
          });
        },
        onSignUpSuccess: async (data) => {
          await purchase({
            items,
            publicContractId: data?.publicContract?._id,
            customerEmail: data.user.email,
          });
        },
      });
    } else {
      // mutateRecover({
      //   templateId: documentId,
      //   document: editorState?.document,
      //   tenantId: profile.organization,
      // });
      // create public contract
      const data = {
        publicContract: {
          _id: '',
        },
      };

      await purchase({
        items,
        publicContractId: data?.publicContract?._id,
        customerEmail: profile.email,
      });
    }
  };
  function handleShieldChange(option) {
    editorDispatch({ type: SET_SHIELD_TYPE, shieldType: option });
  }
  const shieldList = useMemo(() => {
    return Array.from(Array(2).keys()).map((number) => ({
      value: `shield${number + 1}`,
      label: t(`shield`, { context: 'number', number: number + 1 }),
      number: number + 1,
    }));
  }, [t]);

  useEffect(() => {
    editorDispatch({ type: SET_SHIELD_TYPE, shieldType: shieldList[0] });
  }, [editorDispatch, shieldList]);
  console.log({ editorState, errors: formik.errors });

  return (
    <div className="bg-white rounded border">
      <div className="">
        {/* {
            currentStep === 1 &&
            <div className="p-4 d-flex justify-content-center align-items-center">
              Intro
            </div>
        } */}
        {currentStep === 1 && (
          <ParticipantForm
            name={`p-${currentParticipant._id}.participant`}
            participant={
              formik.values?.[`p-${currentParticipant._id}`]?.participant
            }
            errors={
              formik?.errors?.[`p-${currentParticipant._id}`]?.participant ?? {}
            }
            touched={
              formik?.touched?.[`p-${currentParticipant._id}`]?.participant ??
              {}
            }
            onChange={formik.handleChange}
          />
        )}
        {currentStep === 2 && (
          <div className="p-2 mt-2">
            <VariableForm
              className="mt-2"
              name={`p-${currentParticipant._id}.variables`}
              loading={false}
              onChange={formik.handleChange}
              variable={currentVariable ?? {}}
              values={
                formik?.values?.[`p-${currentParticipant._id}`]?.variables ?? {}
              }
              errors={
                formik?.errors?.[`p-${currentParticipant._id}`]?.variables ?? {}
              }
              touched={
                formik?.touched?.[`p-${currentParticipant._id}`]?.variables ??
                {}
              }
              onDateChange={handleDateChange}
              onSelectChange={handleSelectChange}
            />
          </div>
        )}
        {currentStep === 3 && (
          <div className="p-4 d-flex flex-column">
            <div>
              <div className="mb-2 font-weight-bold">
                Elige el tipo de blindaje que deseas:
              </div>
              <Select
                options={shieldList}
                value={shieldType || shieldList[0]}
                onChange={handleShieldChange}
                styles={{ menu: (base) => ({ ...base, zIndex: 1000 }) }}
              />
            </div>
            <div className="py-4 d-flex justify-content-between align-items-center">
              <div className="font-weight-bold">Importe total</div>
              <div>{total > 0 ? getPrice(total, 'mxn') : 'Gratis'}</div>
            </div>
          </div>
        )}
      </div>
      <div className="d-flex justify-content-evenly py-4">
        <Button
          color="accent"
          onClick={handlePrev}
          hidden={currentStep === 1 && currentParticipantNumber === 0}
        >
          Regresar
        </Button>
        <Button
          color="accent-100"
          onClick={handleNext}
          hidden={currentStep === 3}
        >
          {'Siguiente'}
        </Button>
        <Button
          color="accent-100"
          onClick={handleSend}
          hidden={currentStep !== 3}
        >
          Enviar
        </Button>
      </div>
    </div>
  );
}

function getSchema(
  participant,
  questions = [],
  attachments = [],
  pendingVariables = [],
  { validatePhone } = {}
) {
  // const allQuestionsAnswers = questions.flatMap(q => q.answers)

  return Yup.lazy((params) => {
    console.log({ params });
    // const currentAnswers = Object.values(params.questions || {});
    // const populatedCurrentAnswers = allQuestionsAnswers.filter(a => currentAnswers.includes(a._id))
    // const allCurrentAnswersVariables = populatedCurrentAnswers.flatMap(a => a.variables);

    const { agendaProperties, ...variablesSchema } =
      participant.variables.reduce((obj, variable) => {
        const variableId =
          variable.type === 'constant'
            ? variable.apiName.replace('.', '-')
            : variable._id;
        if (variable.type === 'fixed' && variable.options?.length) {
          obj[variableId] = Yup.string().required('Required');
        } else if (variable.type === 'date') {
          obj[variableId] = Yup.string();
        } else if (variable.type === 'number') {
          obj[variableId] = Yup.number().required('Required');
        } else {
          obj[variableId] = Yup.string().required('Required');
        }
        return obj;
      }, {});
    console.log({ agendaProperties });

    return Yup.object().shape({
      // questions: Yup.object().shape((Object.keys(params.questions || {})).reduce((obj, questionKey) => {
      //   obj[questionKey] = Yup.string()
      //   return obj;
      // }, {})),
      variables: Yup.object().shape({
        ...variablesSchema,
        agendaProperties: Yup.object().shape(agendaProperties),
      }),
      // pendingVariables: Yup.object().shape(pendingVariables.reduce((obj, variable) => {
      //   const variableId = variable._id;
      //   if (variable.type === 'fixed' && variable.options?.length) {
      //     obj[variableId] =  Yup.string().required('Required')
      //   } else if (variable.type === 'date') {
      //     obj[variableId] = Yup.string()
      //   } else {
      //     obj[variableId] = Yup.string().required('Required')
      //   }
      //   return obj;
      // }, {})),
      participant: getParticipantSchema({ validatePhone }),
      // attachments: Yup.object().shape(Object.keys(params.attachments || {}).reduce((obj, attachmentId) => {
      //   const pAttachment = attachments.find(a => a._id === attachmentId);
      //   // console.log({pAttachment, attachments, attachmentId})
      //   obj[attachmentId] = Yup.object().shape({
      //     url: pAttachment?.required ? Yup.string().required('Required') : Yup.string(),
      //     name:  Yup.string(),
      //   })
      //   return obj;
      // }, {})),
    });
  });
}

function getInitialValues(participant = {}, questions = []) {
  return {
    // questions: questions.reduce((obj, question) => {
    //   obj[question._id] = '' // question.answers[0]._id
    //   return obj;
    // }, {}),
    variables: participant.variables
      .filter((variable) => variable.type && variable.name)
      .reduce((obj, variable) => {
        if (variable.type === 'fixed' && variable.options?.length) {
          obj[variable._id] = variable.options[0];
        } else if (variable.type === 'date') {
          obj[variable._id] = Date.now();
        } else {
          obj[variable._id] = '';
        }
        return obj;
      }, {}),
    // attachments: participant.attachments?.reduce((obj, attachment) => {
    //   obj[attachment._id] = {name: '', url: ''};
    //   return obj;
    // }, {}) || {},
    participant: {
      name: participant.name,
      last_name: participant.last_name,
      representative: participant.representative,
      email: participant.email,
      phone: participant.email,
      label: participant.label,
      obligation: participant.obligation,
    },
  };
}

function ParticipantForm({
  header,
  loading,
  name,
  errors = {},
  touched = {},
  shieldType,
  jurisdiction,
  participant,
  onChange,
  fillMyData,
  showFillMyData,
  onFillMyData,
  onRequestAgreement,
}) {
  const { t } = useTranslation('common');
  const isFillMyDataChecked = fillMyData === name;
  const prefix = name ? `${name}.` : '';

  const cpfLabel =
    jurisdiction === 'pr'
      ? 'DNI/C.E./otro'
      : jurisdiction === 'co'
      ? 'Cédula No.'
      : 'CPF';
  console.log({ participant, errors, touched });

  return (
    <Container className="p-4">
      <h5
        className={clsx(
          { 'text-muted': !header && !participant.label },
          'mb-2'
        )}
      >
        <span className="font-weight-bold">
          {header || participant.label || t('forms.role')}
        </span>
      </h5>

      <Form className="row text-left">
        <Col sm={12}>
          <FormGroup>
            <Label className="mb-1 font-weight-bold" for={`${prefix}name`}>
              {t('publicTemplates.obligationTypeQuestion', {
                context: jurisdiction,
              })}{' '}
              <span className="text-accent">*</span>
            </Label>
            <CustomInput
              type="radio"
              className="mr-2"
              name={`${prefix}obligation`}
              id={'individual'}
              value={'individual'}
              color="accent"
              label={t('participant_individual')}
              checked={participant.obligation === 'individual'}
              onChange={onChange}
            />
            <CustomInput
              type="radio"
              className="mr-2"
              name={`${prefix}obligation`}
              id={'legal'}
              value={'legal'}
              color="accent"
              label={t('participant_legal', { context: jurisdiction })}
              checked={participant.obligation === 'legal'}
              onChange={onChange}
            />
          </FormGroup>
          <FormGroup className="mb-0">
            {participant.obligation === 'legal' && (
              <Label className="mb-1 font-weight-bold" for={`${prefix}name`}>
                {t('publicTemplates.companyName')}{' '}
                <span className="text-accent">*</span>
              </Label>
            )}
            {participant.obligation === 'individual' && (
              <Label className="mb-1 font-weight-bold" for={`${prefix}name`}>
                {t('publicTemplates.name')}{' '}
                <span className="text-accent">*</span>
              </Label>
            )}
            <InputGroup>
              <InputWithLoader
                type="text"
                className="form-control"
                id={`${prefix}name`}
                name={`${prefix}name`}
                // placeholder={t('forms.firstName')}
                onChange={onChange}
                value={participant.name}
                loading={loading}
                invalid={errors.name && touched.name}
              />
              <FormFeedback>{t(`errors.${errors.name}`)}</FormFeedback>
            </InputGroup>
          </FormGroup>
        </Col>
        {participant.obligation === 'individual' && (
          <Col sm={12}>
            <FormGroup className="mb-0">
              <Label
                className="mb-1 font-weight-bold"
                for={`${prefix}last_name`}
              >
                {t('publicTemplates.lastName')}{' '}
                <span className="text-accent">*</span>
              </Label>
              <InputGroup>
                <InputWithLoader
                  type="text"
                  className="form-control"
                  id={`${prefix}last_name`}
                  name={`${prefix}last_name`}
                  // placeholder={t('forms.lastName')}
                  onChange={onChange}
                  value={participant.last_name}
                  loading={loading}
                  invalid={errors.last_name && touched.last_name}
                />
                <FormFeedback>{t(`errors.${errors.last_name}`)}</FormFeedback>
              </InputGroup>
            </FormGroup>
          </Col>
        )}
        {participant.obligation === 'legal' && (
          <Col sm={12}>
            <FormGroup className="mb-0">
              <Label
                className="mb-1 font-weight-bold"
                for={`${prefix}representative`}
              >
                {t('publicTemplates.representative')}{' '}
                <span className="text-accent">*</span>
              </Label>
              <InputGroup>
                <InputWithLoader
                  type="text"
                  className="form-control"
                  id={`${prefix}representative`}
                  name={`${prefix}representative`}
                  // placeholder={t('forms.representative')}
                  onChange={onChange}
                  value={participant.representative}
                  loading={loading}
                  invalid={errors.representative && touched.representative}
                />
                <FormFeedback>
                  {t(`errors.${errors.representative}`)}
                </FormFeedback>
              </InputGroup>
            </FormGroup>
          </Col>
        )}
        <Col sm={12} className="my-3">
          <FormGroup className="mb-0">
            <Label className="mb-1 font-weight-bold" for={`${prefix}email`}>
              {t('publicTemplates.email', { context: participant.obligation })}{' '}
              <span className="text-accent">*</span>
            </Label>
            <InputGroup>
              <InputWithLoader
                type="email"
                className="form-control"
                id={`${prefix}email`}
                name={`${prefix}email`}
                // placeholder={t('forms.email')}
                onChange={onChange}
                value={participant.email}
                loading={loading}
                invalid={errors.email && touched.email}
              />
              <FormFeedback>{t(`errors.${errors.email}`)}</FormFeedback>
            </InputGroup>
          </FormGroup>
        </Col>
        <Col sm={12}>
          <FormGroup className="form-group mb-0">
            <Label className="mb-1 font-weight-bold" for={`${prefix}phone`}>
              {t('publicTemplates.phone', { context: participant.obligation })}
            </Label>
            <InputGroup>
              {/* <PhoneNumberInput
                    className="form-control" 
                    id={`${prefix}phone`} 
                    name={`${prefix}phone`}
                    // placeholder={t('forms.phoneNumber')}
                    onChange={onChange}  
                    value={participant.phone}
                    loading={loading}
                    invalid={errors.phone && touched.phone}
                  /> */}
              <InputWithLoader
                type="text"
                className="form-control"
                id={`${prefix}phone`}
                name={`${prefix}phone`}
                // placeholder={t('forms.phoneNumber')}
                onChange={onChange}
                value={participant.phone}
                loading={loading}
                invalid={errors.phone && touched.phone}
              />
              <FormFeedback>{t(`errors.${errors.phone}`)}</FormFeedback>
            </InputGroup>
          </FormGroup>
        </Col>
        <Col sm={12} hidden={!['br'].includes(jurisdiction)}>
          <FormGroup className="mb-0">
            <Label className="mb-1 font-weight-bold" for={`${prefix}cargo`}>
              {t('Cargo')} <span className="text-accent">*</span>
            </Label>
            <InputGroup>
              <InputWithLoader
                type="text"
                className="form-control"
                id={`${prefix}cargo`}
                name={`${prefix}cargo`}
                // placeholder={t('forms.lastName')}
                onChange={onChange}
                value={participant.cargo}
                loading={loading}
                invalid={errors.cargo && touched.cargo}
              />
              <FormFeedback>{t(`errors.${errors.cargo}`)}</FormFeedback>
            </InputGroup>
          </FormGroup>
        </Col>
        <Col sm={12} hidden={!['br'].includes(jurisdiction)}>
          <FormGroup className="mb-0">
            <Label className="mb-1 font-weight-bold" for={`${prefix}rg`}>
              {t('RG')} <span className="text-accent">*</span>
            </Label>
            <InputGroup>
              <InputWithLoader
                type="text"
                className="form-control"
                id={`${prefix}rg`}
                name={`${prefix}rg`}
                // placeholder={t('forms.lastName')}
                onChange={onChange}
                value={participant.rg}
                loading={loading}
                invalid={errors.rg && touched.rg}
              />
              <FormFeedback>{t(`errors.${errors.rg}`)}</FormFeedback>
            </InputGroup>
          </FormGroup>
        </Col>
        <Col sm={12} hidden={!['br', 'co', 'pr'].includes(jurisdiction)}>
          <FormGroup className="mb-0">
            <Label className="mb-1 font-weight-bold" for={`${prefix}cpf`}>
              {t(cpfLabel)} <span className="text-accent">*</span>
            </Label>
            <InputGroup>
              <InputWithLoader
                type="text"
                className="form-control"
                id={`${prefix}cpf`}
                name={`${prefix}cpf`}
                // placeholder={t('forms.lastName')}
                onChange={onChange}
                value={participant.cpf}
                loading={loading}
                invalid={errors.cpf && touched.cpf}
              />
              <FormFeedback>{t(`errors.${errors.cpf}`)}</FormFeedback>
            </InputGroup>
          </FormGroup>
        </Col>
      </Form>
    </Container>
  );
}

function VariableForm({
  className,
  variable,
  name = 'variables',
  onChange,
  loading,
  values,
  errors = {},
  touched = {},
  onSelectChange,
  onDateChange,
}) {
  const { t } = useTranslation('common');
  const prefix = name ? `${name}.` : '';
  const isConstant = variable.type === 'constant';
  const variableId = isConstant
    ? variable.apiName.replace('.', '-')
    : variable._id;

  console.log({ variable, errors, variableId, e: errors[variableId] });

  const DateInput = React.forwardRef((props, ref) => (
    <input className="form-control" {...props} ref={ref} />
  ));

  return (
    <Element name={`v-${variable._id}`} className={clsx('element', className)}>
      <Col key={variable._id} id={variable._id} sm={12} className="mb-1">
        <FormGroup className="mb-0">
          <Label
            className="mb-1 font-weight-bold text-muted"
            for={`${prefix}email`}
          >
            {variable.name} <span className="text-accent">*</span>
          </Label>
          <div>
            {['fixed'].includes(variable.type) && variable?.options?.length ? (
              <>
                <Select
                  options={variable.options.map((opt) => ({
                    value: opt,
                    label: opt,
                  }))}
                  // isLoading={isSubmitting}
                  // isDisabled={isSubmitting}

                  styles={reactSelectStyles}
                  className="w-100"
                  onChange={onSelectChange(variableId, name)}
                  value={{
                    label: values[variableId],
                    value: values[variableId],
                  }}
                />
                <small className="text-danger">
                  {t(`errors.${errors[variableId]}`)}
                </small>
              </>
            ) : ['date'].includes(variable.type) ? (
              <DatePickerContainer>
                <DatePicker
                  locale="es"
                  fullWidth
                  selected={
                    values[variableId] ? new Date(values[variableId]) : null
                  }
                  onChange={onDateChange(variableId, name)}
                  dateFormat="dd/MM/yyyy"
                  // customInput={<DateInput />}
                  className="form-control"
                  showYearDropdown
                  showMonthDropdown
                />
                <small className="text-danger">
                  {t(`errors.${errors[variableId]}`)}
                </small>
              </DatePickerContainer>
            ) : (
              <InputGroup>
                <InputWithLoader
                  type={variable.type}
                  className="form-control"
                  id={`${name}.${variableId}`}
                  name={`${name}.${variableId}`}
                  // placeholder={variable.name}
                  onChange={onChange}
                  value={values[variableId]}
                  maxLength={
                    variable.type === 'text' && variable.limit
                      ? variable.limit
                      : 10000
                  }
                  size={
                    variable.type === 'text' && variable.limit
                      ? variable.limit
                      : 10000
                  }
                  loading={loading}
                  invalid={errors[variableId] && touched[variableId]}
                />
                <FormFeedback>{t(`errors.${errors[variableId]}`)}</FormFeedback>
              </InputGroup>
            )}
          </div>
        </FormGroup>
      </Col>
    </Element>
  );
}
